// #web-alerts-badge: web port of the mobile useAlertsCount hook in
// apps/mobile/app/(app)/_layout.tsx. Three polled queries summed into
// a single integer the sidebar badge can render.
//
// All three queries are namespaced with a 'badge' segment so they
// CANNOT collide with the per-page consumers — even though they hit
// the same backend endpoints. The badge queryFns return scalar
// counts (numbers), while the page-level consumers
// (usePendingNotifications, useChatList, usePendingMergeProposals)
// return arrays of typed items. Sharing the cache between them
// would crash the page's `.map(...)` calls as soon as the badge
// query won the race to populate the cache — exactly the
// #notifications-map-error symptom that surfaced post-d2cdca3
// when lazy-loading /notifications made AppShell's badge query
// reliably win.
//
//   - ['notifications', 'badge', userId]   distinct from
//                                          ['notifications', userId] used by
//                                          usePendingNotifications
//   - ['chat-list-badge', userId]          distinct from useChatList's
//                                          ['chat-list']
//   - ['mergeProposals', 'badge', userId]  distinct from
//                                          ['mergeProposals', 'pending', userId]
//                                          used by usePendingMergeProposals
//
// All three poll every 30s. Sum drives the sidebar badge — count > 0
// shows the badge, count = 0 hides it.

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../client'

interface NotificationRow {
  // structure varies; only the array length matters for badge
  [k: string]: unknown
}

interface ChatRow {
  unreadCount?: number | null
}

interface MergeProposalRow {
  status?: string | null
  owner_a_id?: number | null
  owner_b_id?: number | null
  owner_a_decision?: string | null
  owner_b_decision?: string | null
}

export function useAlertsCount(userId: number | undefined): number {
  const { data: requestCount } = useQuery({
    queryKey: ['notifications', 'badge', userId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/notifications/pending/${userId}`)
      const list = Array.isArray(res.data) ? res.data : (res.data?.requests ?? [])
      return (list as NotificationRow[]).length
    },
    enabled: !!userId,
    refetchInterval: 30_000,
  })

  const { data: unreadMessages } = useQuery({
    queryKey: ['chat-list-badge', userId],
    queryFn: async () => {
      const res = await apiClient.get('/api/chat/chat-list')
      const chats = Array.isArray(res.data) ? res.data : (res.data?.chats ?? [])
      return (chats as ChatRow[]).reduce((sum, c) => sum + (c.unreadCount ?? 0), 0)
    },
    enabled: !!userId,
    refetchInterval: 30_000,
  })

  const { data: pendingMergeCount } = useQuery({
    queryKey: ['mergeProposals', 'badge', userId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/merge-proposals/pending/${userId}`)
      const list = Array.isArray(res.data) ? res.data : []
      // Filter mirrors RequestsTab + mobile useAlertsCount — count only
      // items needing THIS user's action. snake_case keys come straight
      // from the raw-SQL route (route bypasses Drizzle's mode='number'
      // cast on bigserial, so owner ids land as strings; coerce).
      return (list as MergeProposalRow[]).filter((p) =>
        p.status === 'pending'
        || (p.status === 'confirmed_by_one' && (
          (Number(p.owner_a_id) === userId && p.owner_a_decision == null)
          || (Number(p.owner_b_id) === userId && p.owner_b_decision == null)
        ))
      ).length
    },
    enabled: !!userId,
    refetchInterval: 30_000,
  })

  return (requestCount ?? 0) + (unreadMessages ?? 0) + (pendingMergeCount ?? 0)
}
